import type { useRequestSections } from '../api/request';
import {
  getComplaintRowsFromSearchResponse,
  getDashboardLookupsFromResponse,
  getGenericLookupGroupsFromResponse,
  getNextPageTokenFromSearchResponse,
  isAssignedToComplaintUser,
} from './complaintDashboardUtils';
import type { ComplaintAssigneeOption, ComplaintDashboardRow } from './complaintDashboardTypes';
import type {
  CaseStatus,
  ComplaintIntakeData,
  ComplaintLookupSet,
  ResolutionData,
  RiskLevel,
} from './complaintIntakeUtils';
import { firstValue, isFieldRecord, textFromValue } from './complaintIntakeUtils';
import type { IntakeRouting2Resource } from './intakeRouting2';

type ComplaintApi = ReturnType<typeof useRequestSections>['Complaint'];
type CaseApi = ReturnType<typeof useRequestSections>['Case'];
type UserApi = ReturnType<typeof useRequestSections>['User'];

type CacheEntry<T> = {
  expiresAt: number;
  promise?: Promise<T>;
  value?: T;
};

const CACHE_TTL_MS = 45_000;
const PAGE_SIZE = 200;
const CURRENT_USER_ASSIGNED_CACHE_KEY = '__currentUserAssigned__';
const STORAGE_PREFIX = 'safeviewComplaintCache:v1:';
const STORAGE_KEYS = {
  applicationUsers: 'applicationUsers',
  complaintLookups: 'complaintLookups',
  allRows: 'allRows',
} as const;
const DRAFT_STORAGE_PREFIX = `${STORAGE_PREFIX}draft:`;
let newComplaintDraftGeneration = 0;

export const getNewComplaintDraftGeneration = () => newComplaintDraftGeneration;

export const clearNewComplaintDrafts = () => {
  // Invalidate pending autosaves before the hash navigation unmounts the form.
  newComplaintDraftGeneration += 1;
  try {
    const storage = getSessionStorage();
    if (!storage) return;
    const keys = Array.from({ length: storage.length }, (_, index) => storage.key(index));
    for (const key of keys) {
      if (key === `${DRAFT_STORAGE_PREFIX}new` || key?.startsWith(`${DRAFT_STORAGE_PREFIX}new:`)) {
        storage.removeItem(key);
      }
    }
  } catch {
    // Browser storage failures must not block starting a fresh form.
  }
};

export type ComplaintDraftAutosaveState = {
  formData: ComplaintIntakeData;
  routingResource?: IntakeRouting2Resource;
  assignmentManuallyChanged?: boolean;
  resolutionData: ResolutionData;
  caseStatus: CaseStatus;
  riskLevel?: RiskLevel;
  additionalFieldValues?: Record<string, string | string[]>;
  savedAt: number;
};

let lookupCache: CacheEntry<ComplaintLookupSet> | undefined;
let allRowsCache: CacheEntry<ComplaintDashboardRow[]> | undefined;
let applicationUsersCache: CacheEntry<ComplaintAssigneeOption[]> | undefined;
const assignedRowsCache = new Map<string, CacheEntry<ComplaintDashboardRow[]>>();

const isFresh = <T>(entry?: CacheEntry<T>) => Boolean(entry?.value && entry.expiresAt > Date.now());

const readCacheValue = <T>(entry?: CacheEntry<T>) => entry?.value;

const getSessionStorage = () => {
  try {
    if (typeof globalThis === 'undefined') return;
    return globalThis.sessionStorage;
  } catch {
    return;
  }
};

const storageKey = (key: string) => `${STORAGE_PREFIX}${key}`;
const draftStorageKey = (key: string) => `${DRAFT_STORAGE_PREFIX}${key}`;

const removeStoredCache = (key: string) => {
  try {
    getSessionStorage()?.removeItem(storageKey(key));
  } catch {
    // Browser storage failures should not block the API path.
  }
};

const readStoredCache = <T>(key: string): CacheEntry<T> | undefined => {
  const storage = getSessionStorage();
  if (!storage) return;

  try {
    const rawValue = storage.getItem(storageKey(key));
    if (!rawValue) return;

    const parsed = JSON.parse(rawValue) as Partial<CacheEntry<T>>;
    if (typeof parsed.expiresAt !== 'number' || parsed.value === undefined) {
      removeStoredCache(key);
      return;
    }

    if (parsed.expiresAt <= Date.now()) {
      removeStoredCache(key);
      return;
    }

    return {
      expiresAt: parsed.expiresAt,
      value: parsed.value,
    };
  } catch {
    removeStoredCache(key);
    return;
  }
};

const writeStoredCache = <T>(key: string, entry: CacheEntry<T>) => {
  if (entry.value === undefined) return;

  try {
    getSessionStorage()?.setItem(
      storageKey(key),
      JSON.stringify({ expiresAt: entry.expiresAt, value: entry.value })
    );
  } catch {
    // Keep the in-memory cache path even when browser storage is unavailable.
  }
};

const hydrateCacheEntry = <T>(entry: CacheEntry<T> | undefined, persistedKey?: string) =>
  entry || (persistedKey ? readStoredCache<T>(persistedKey) : undefined);

const setCacheValue = <T>(entry: CacheEntry<T>, value: T) => {
  entry.value = value;
  entry.promise = undefined;
  entry.expiresAt = Date.now() + CACHE_TTL_MS;
  return value;
};

const loadWithCache = async <T>(
  currentEntry: CacheEntry<T> | undefined,
  setEntry: (entry: CacheEntry<T>) => void,
  loader: () => Promise<T>,
  persistedKey?: string
) => {
  const activeEntry = hydrateCacheEntry(currentEntry, persistedKey);
  if (activeEntry && activeEntry !== currentEntry) setEntry(activeEntry);

  const cachedValue = activeEntry?.value;
  if (activeEntry && cachedValue !== undefined && activeEntry.expiresAt > Date.now()) {
    return cachedValue;
  }
  if (activeEntry?.promise) return activeEntry.promise;

  const entry = activeEntry || { expiresAt: 0 };
  setEntry(entry);
  entry.promise = loader()
    .then((value) => {
      const cached = setCacheValue(entry, value);
      if (persistedKey) writeStoredCache(persistedKey, entry);
      return cached;
    })
    .catch((error) => {
      entry.promise = undefined;
      throw error;
    });

  return entry.promise;
};

const searchRows = async (
  caseApi: CaseApi,
  lookups: ComplaintLookupSet,
  assignedToUser?: string,
  useAuthenticatedUser = false
) => {
  const isAssignedSearch = assignedToUser !== undefined;
  const scopedAssignedToUser = assignedToUser?.trim() || '';

  if (isAssignedSearch && !scopedAssignedToUser && !useAuthenticatedUser) {
    return [];
  }

  let pageToken = '';
  const rows: ComplaintDashboardRow[] = [];

  for (let page = 0; page < 25; page += 1) {
    const response = await (
      useAuthenticatedUser ? caseApi.searchAssignedToCurrentUserCases : caseApi.searchCases
    )({
      filter: {
        ...(isAssignedSearch && !useAuthenticatedUser
          ? { assignedToUser: scopedAssignedToUser }
          : {}),
        pageSize: PAGE_SIZE,
        ...(pageToken ? { pageToken } : {}),
      },
    });

    rows.push(...getComplaintRowsFromSearchResponse(response, lookups));
    pageToken = getNextPageTokenFromSearchResponse(response);
    if (!pageToken) break;
  }

  return isAssignedSearch && !useAuthenticatedUser
    ? rows.filter((row) => isAssignedToComplaintUser(row, scopedAssignedToUser))
    : rows;
};

const getAssignableUserRecords = (payload: unknown) => {
  const source = isFieldRecord(payload) && isFieldRecord(payload.data) ? payload.data : payload;
  const response =
    isFieldRecord(source) && isFieldRecord(source.response)
      ? source.response
      : isFieldRecord(source) && isFieldRecord(source.Response)
        ? source.Response
        : source;
  const records = isFieldRecord(response)
    ? response.users ||
      response.Users ||
      response.assignableUsers ||
      response.AssignableUsers ||
      response.answers ||
      response.Answers
    : undefined;

  return Array.isArray(records) ? records.filter((record) => isFieldRecord(record)) : [];
};

const mapApplicationUsers = (payload: unknown): ComplaintAssigneeOption[] => {
  const options = new Map<string, ComplaintAssigneeOption>();

  for (const user of getAssignableUserRecords(payload)) {
    const safeviewUserId = firstValue(user, [
      'safeviewUserId',
      'safeview_user_id',
      'SafeviewUserId',
      'SafeViewUserId',
      'userId',
      'UserId',
      'userid',
      'USERID',
      'key',
      'Key',
    ]).trim();
    if (!safeviewUserId) continue;

    const normalized = safeviewUserId.toLowerCase();
    if (options.has(normalized)) continue;

    options.set(normalized, {
      safeviewUserId,
      label:
        firstValue(user, [
          'displayName',
          'display_name',
          'DisplayName',
          'outputText',
          'OutputText',
          'output_text',
          'name',
          'Name',
        ]) || safeviewUserId,
      assignedUserId: '',
      source: 'directory',
    });
  }

  return Array.from(options.values()).sort((first, second) =>
    textFromValue(first.label).localeCompare(textFromValue(second.label))
  );
};

const getLookupCache = () => {
  lookupCache = hydrateCacheEntry(lookupCache, STORAGE_KEYS.complaintLookups);
  return lookupCache;
};

const getAllRowsCache = () => {
  allRowsCache = hydrateCacheEntry(allRowsCache, STORAGE_KEYS.allRows);
  return allRowsCache;
};

const getApplicationUsersCache = () => {
  applicationUsersCache = hydrateCacheEntry(applicationUsersCache, STORAGE_KEYS.applicationUsers);
  return applicationUsersCache;
};

export const getCachedComplaintLookups = () => readCacheValue(getLookupCache());

export const loadComplaintLookups = (complaintApi: ComplaintApi) =>
  loadWithCache(
    lookupCache,
    (entry) => {
      lookupCache = entry;
    },
    async () => {
      const [typedResponse, genericResponse] = await Promise.all([
        complaintApi.listLookups(),
        complaintApi.listLookupGroups().catch(() => undefined),
      ]);
      return {
        ...getDashboardLookupsFromResponse(typedResponse),
        lookupGroups: getGenericLookupGroupsFromResponse(genericResponse),
      };
    },
    STORAGE_KEYS.complaintLookups
  );

export const getCachedAllComplaintRows = () => readCacheValue(getAllRowsCache());

export const loadAllComplaintRows = (lookups: ComplaintLookupSet, caseApi: CaseApi) =>
  loadWithCache(
    allRowsCache,
    (entry) => {
      allRowsCache = entry;
    },
    async () => searchRows(caseApi, lookups),
    STORAGE_KEYS.allRows
  );

export const getCachedAssignedComplaintRows = (
  currentUser: string,
  options?: { useAuthenticatedUser?: boolean }
) => {
  const useAuthenticatedUser = Boolean(options?.useAuthenticatedUser);
  if (!currentUser.trim() && !useAuthenticatedUser) return [];

  const allRows = readCacheValue(getAllRowsCache());
  if (allRows && !useAuthenticatedUser) {
    return allRows.filter((row) => isAssignedToComplaintUser(row, currentUser));
  }

  const cacheKey = useAuthenticatedUser ? CURRENT_USER_ASSIGNED_CACHE_KEY : currentUser;
  return readCacheValue(assignedRowsCache.get(cacheKey));
};

export const loadAssignedComplaintRows = (
  lookups: ComplaintLookupSet,
  currentUser: string,
  options: { useAuthenticatedUser?: boolean } | undefined,
  caseApi: CaseApi
) => {
  const useAuthenticatedUser = Boolean(options?.useAuthenticatedUser);
  if (!currentUser.trim() && !useAuthenticatedUser) return Promise.resolve([]);

  const currentAllRowsCache = getAllRowsCache();
  if (isFresh(currentAllRowsCache) && !useAuthenticatedUser) {
    return Promise.resolve(
      (currentAllRowsCache?.value || []).filter((row) =>
        isAssignedToComplaintUser(row, currentUser)
      )
    );
  }

  const cacheKey = useAuthenticatedUser ? CURRENT_USER_ASSIGNED_CACHE_KEY : currentUser;
  return loadWithCache(
    assignedRowsCache.get(cacheKey),
    (entry) => assignedRowsCache.set(cacheKey, entry),
    async () => searchRows(caseApi, lookups, currentUser, useAuthenticatedUser)
  );
};

export const getCachedApplicationUsers = () => readCacheValue(getApplicationUsersCache());

export const loadApplicationUsers = (userApi: UserApi) =>
  loadWithCache(
    applicationUsersCache,
    (entry) => {
      applicationUsersCache = entry;
    },
    async () => mapApplicationUsers(await userApi.listApplicationUsers('')),
    STORAGE_KEYS.applicationUsers
  );

export const loadComplaintAssignableUsers = (complaintApi: ComplaintApi) =>
  loadWithCache(
    applicationUsersCache,
    (entry) => {
      applicationUsersCache = entry;
    },
    async () => mapApplicationUsers(await complaintApi.listAssignableUsers()),
    STORAGE_KEYS.applicationUsers
  );

export const invalidateComplaintReadCache = () => {
  allRowsCache = undefined;
  assignedRowsCache.clear();
  removeStoredCache(STORAGE_KEYS.allRows);
};

export const invalidateComplaintCaches = () => {
  invalidateComplaintReadCache();
};

export const getCachedComplaintDraft = (key: string) => {
  if (!key) return;

  const storage = getSessionStorage();
  if (!storage) return;

  try {
    const rawValue = storage.getItem(draftStorageKey(key));
    if (!rawValue) return;

    const draft = JSON.parse(rawValue) as Partial<ComplaintDraftAutosaveState>;
    if (
      !isFieldRecord(draft.formData) ||
      !isFieldRecord(draft.resolutionData) ||
      !draft.caseStatus ||
      typeof draft.savedAt !== 'number'
    ) {
      storage.removeItem(draftStorageKey(key));
      return;
    }

    return draft as ComplaintDraftAutosaveState;
  } catch {
    storage.removeItem(draftStorageKey(key));
    return;
  }
};

export const storeComplaintDraft = (key: string, draft: ComplaintDraftAutosaveState) => {
  if (!key) return;

  try {
    getSessionStorage()?.setItem(draftStorageKey(key), JSON.stringify(draft));
  } catch {
    // Draft autosave should never block the primary complaint workflow.
  }
};

export const clearComplaintDraft = (key: string) => {
  if (!key) return;

  try {
    getSessionStorage()?.removeItem(draftStorageKey(key));
  } catch {
    // Ignore browser storage failures.
  }
};
